import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import type { ReservationSlotTemplate } from '../../../../domain/reservationSlots';
import type { UseReservationSlotSettingsReturn } from '../hooks/useReservationSlotSettings';
import SettingsPanel from './SettingsPanel';

export interface SettingsDialogProps {
  open: boolean;
  templates: ReservationSlotTemplate[];
  settings: UseReservationSlotSettingsReturn;
  onClose: () => void;
}

/**
 * 基本設定ダイアログ
 *
 * @description
 * デフォルトテンプレートと医師個別の上書きを編集する `SettingsPanel` をモーダルで表示する。
 * 編集UIは `SettingsPanel` に一本化しており、本コンポーネントは枠と保存操作のみを持つ。
 * 保存に成功した場合のみダイアログを閉じる。
 */
const SettingsDialog: React.FC<SettingsDialogProps> = ({
  open,
  templates,
  settings,
  onClose,
}) => {
  const { save, saving } = settings;

  /**
   * 保存して、成功した場合のみダイアログを閉じる
   *
   * @returns {Promise<void>}
   */
  const handleSave = useCallback(async () => {
    const ok = await save();
    if (ok) {
      onClose();
    }
  }, [save, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box>
          <Typography component="div" variant="h6" fontWeight={700}>
            基本設定と個別上書き
          </Typography>
          <Typography variant="body2" color="text.secondary">
            医師ごとのテンプレート割り当てを管理します
          </Typography>
        </Box>
        <IconButton size="small" aria-label="閉じる" onClick={onClose} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <SettingsPanel templates={templates} settings={settings} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          キャンセル
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          保存する
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog;
